var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { TextSelection } from 'prosemirror-state';
import { RemoveMarkStep } from 'prosemirror-transform';
var isEmptyDoc = function (doc) {
    return doc.childCount === 1 && !doc.firstChild.content.size && doc.firstChild.type === doc.type.contentMatch.defaultType;
};
var isOriginAttrs = function (node, nodeType) {
    if (nodeType === void 0) { nodeType = node.type; }
    var typeAttrs = nodeType.spec.attrs;
    if (!typeAttrs)
        return;
    var attrNames = Object.keys(typeAttrs);
    return !attrNames.some(function (name) {
        if (typeof typeAttrs[name].default === undefined || node.attrs[name] === typeAttrs[name].default)
            return false;
        return true;
    });
};
var findCutBefore = function ($pos) {
    if (!$pos.parent.type.spec.isolating) {
        for (var i = $pos.depth - 1; i >= 0; i--) {
            if ($pos.index(i) > 0) {
                return $pos.doc.resolve($pos.before(i + 1));
            }
            if ($pos.node(i).type.spec.isolating) {
                break;
            }
        }
    }
    return null;
};
var getPrevNodeLastTextBlock = function ($pos) {
    var curNode = $pos.nodeBefore;
    var depth = 0;
    var pos = $pos.pos;
    while (curNode) {
        pos = $pos.pos - curNode.nodeSize - depth;
        if (curNode.isTextblock) {
            break;
        }
        curNode = curNode.lastChild;
        depth++;
    }
    return { node: curNode, pos: pos };
};
var REMOVE_ALL = '_';
var splitStr = function (str) { return (str ? str.split(/\s+/) : []); };
// get information about excludeMarks and remain marks
var handleSpecInfo = function (node, excludeMarkList, whiteList) {
    if (!node)
        return false;
    var _excludeMarkList = splitStr(node.type.spec.excludeMarks);
    var _whiteList = splitStr(node.type.spec.marks);
    if (excludeMarkList.includes(REMOVE_ALL))
        return true;
    if (whiteList.includes(''))
        return true;
    excludeMarkList.push.apply(excludeMarkList, _excludeMarkList);
    _whiteList.push.apply(_whiteList, _whiteList);
    return false;
};
// get the content of `excludeMarks` of the node at the current position, and return a list of compatible marks
var getFitMarksHandler = function ($pos) {
    var depth = $pos.depth + 1;
    var excludeMarkList = [];
    var whiteList = [];
    var removeAll = false;
    var pointerNode = $pos.nodeAfter;
    removeAll = handleSpecInfo(pointerNode, excludeMarkList, whiteList);
    while (depth--) {
        var parentNode = $pos.node(depth);
        removeAll = handleSpecInfo(parentNode, excludeMarkList, whiteList);
    }
    if (excludeMarkList.length + whiteList.length === 0)
        return;
    return function (marks) {
        if (removeAll)
            return [];
        return marks.filter(function (mark) {
            if (excludeMarkList.includes(mark.type.name))
                return false;
            if (whiteList.length && !whiteList.includes(mark.type.name))
                return false;
            return true;
        });
    };
};
// remove the mark that Node cannot contain, identify the custom `spec.excludeMarks`
var removeExcludesMarks = function (tr) {
    var _a = tr.selection, $from = _a.$from, $to = _a.$to;
    var isRemoved = false;
    tr.doc.nodesBetween($from.depth ? $from.before() : $from.pos, $to.depth ? $to.after() : $to.pos, function (node, pos, parent, index) {
        if (node.isLeaf && node.marks.length) {
            var fitRule = getFitMarksHandler(tr.doc.resolve(pos));
            if (fitRule) {
                var newMarks = fitRule(node.marks);
                if (newMarks.length === node.marks.length)
                    return;
                parent.content = parent.content.replaceChild(index, node.isText
                    ? node.type.schema.text(node.textContent, newMarks)
                    : node.type.create(node.attrs, node.content, newMarks));
                isRemoved = true;
            }
        }
        return !node.isLeaf;
    });
    return isRemoved;
};
var getUnInheritAttr = function (node) {
    if (!node || !node.type.spec.attrs)
        return [];
    return Object.keys(node.type.spec.attrs).filter(function (key) {
        var attrSpec = node.type.spec.attrs;
        if (!attrSpec)
            return false;
        return attrSpec[key].inherit === false;
    });
};
// reset the attrs of the node in current selection whose `spec.inherit` is false,
var resetUnInheritAttr = function (oldState, newState) {
    if (oldState.doc === newState.doc)
        return;
    var $oFrom = oldState.selection.$from;
    var $nFrom = newState.selection.$from;
    if (!$oFrom.depth || !$nFrom.depth || $oFrom.before() === $nFrom.before()) {
        return;
    }
    var oldNode = $oFrom.parent;
    var newNode = $nFrom.parent;
    if (oldNode.type !== newNode.type || oldNode === newNode)
        return;
    getUnInheritAttr(oldNode).forEach(function (key) {
        var _a;
        if (oldNode.attrs[key] === newNode.attrs[key]) {
            if (oldNode.attrs === newNode.attrs) {
                newNode.attrs = __assign(__assign({}, newNode.attrs), (_a = {}, _a[key] = newNode.type.attrs[key].default, _a));
            }
            else {
                newNode.attrs[key] = newNode.type.attrs[key].default;
            }
        }
    });
};
// in the case of multiple ranges, the number of pos is incorrect
var getRealSelectionInfo = function (selection) {
    var from = selection.from, to = selection.to;
    var isReverse = selection.anchor > selection.head;
    selection.ranges.forEach(function (_a) {
        var $from = _a.$from, $to = _a.$to;
        from = Math.min($from.pos, from);
        to = Math.max($to.pos, to);
    });
    return { from: from, to: to, anchor: isReverse ? to : from, head: isReverse ? from : to };
};
var removeMark = function (doc, tr, from, to) {
    var matched = [];
    var step = 0;
    doc.nodesBetween(from, to, function (node, pos) {
        if (!node.isInline)
            return;
        step++;
        var toRemove = node.marks;
        if (toRemove && toRemove.length) {
            var end = Math.min(pos + node.nodeSize, to);
            for (var i = 0; i < toRemove.length; i++) {
                var style = toRemove[i];
                var found = void 0;
                for (var j = 0; j < matched.length; j++) {
                    var m = matched[j];
                    if (m.step === step - 1 && style.eq(matched[j].style))
                        found = m;
                }
                if (found) {
                    found.to = end;
                    found.step = step;
                }
                else {
                    // not clear Mark which decare `notClear === true`
                    if (!style.type.spec.notClear) {
                        matched.push({ style: style, from: Math.max(pos, from), to: end, step: step });
                    }
                }
            }
        }
    });
    matched.forEach(function (m) { return tr.step(new RemoveMarkStep(m.from, m.to, m.style)); });
    return tr;
};
var tryReplaceEmpty = function (tr, $from, node) {
    var isInlineNode = node.type.isInline;
    if (!isInlineNode &&
        $from.depth &&
        !$from.parent.childCount &&
        validateNodeContent($from.node($from.depth - 1), node)) {
        var startPos = $from.before();
        tr.replaceWith(startPos, $from.after(), node);
        var $pos = tr.doc.resolve(tr.selection.$to.depth ? tr.selection.$to.after() : tr.selection.to);
        if (tr.selection.from === startPos && (!$pos.nodeAfter || $pos.nodeAfter.isTextblock)) {
            tr.setSelection(TextSelection.create(tr.doc, $pos.pos));
        }
        return true;
    }
    return false;
};
var getStoreMarks = function (state) {
    return state.tr.storedMarks || (state.selection.$to.parentOffset && state.selection.$from.marks()) || [];
};
var validateNodeContent = function (parent, child) {
    if (child.type.name === 'doc') {
        var isMatch_1 = true;
        child.content.forEach(function (n) {
            if (!isMatch_1 || !parent.type.contentMatch.matchType(n.type)) {
                isMatch_1 = false;
            }
        });
        return isMatch_1;
    }
    return Boolean(parent.type.contentMatch.matchType(child.type));
};
var getChildTextblockType = function (nodeType) {
    var edgeCount = nodeType.contentMatch.edgeCount;
    for (var i = 0; i < edgeCount; i++) {
        var childType = nodeType.contentMatch.edge(i).type;
        if (childType.isTextblock) {
            return childType;
        }
    }
};
export { findCutBefore, getChildTextblockType, getPrevNodeLastTextBlock, getRealSelectionInfo, getStoreMarks, getUnInheritAttr, isEmptyDoc, isOriginAttrs, removeExcludesMarks, removeMark, resetUnInheritAttr, tryReplaceEmpty, validateNodeContent, };
//# sourceMappingURL=prose-util.js.map